
import { supabase } from "@/integrations/supabase/client";
import { Quote, QuoteExportOptions } from "@/types/quote";

/**
 * Exports the given items to a CSV file
 * @param items - The items to export
 * @param filename - The name of the file to export to
 */
export const exportToCSV = <T extends { [key: string]: any }>(
  items: T[],
  filename: string = 'export'
) => {
  try {
    // Get the keys from the first item to use as header
    const firstItem = items[0] || {};
    const keys = Object.keys(firstItem);
    const header = keys;
    
    // Convert items to CSV rows based on their structure
    const rows = items.map(item => 
      keys.map(key => item[key] !== undefined ? item[key] : '')
    );
    
    // Combine header and rows
    const csvContent = [
      header.join(','),
      ...rows.map(row => row.map(field => 
        // Escape quotes and wrap fields in quotes
        `"${String(field).replace(/"/g, '""')}"`
      ).join(','))
    ].join('\n');
    
    // Create a blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${formatDate(new Date())}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error("Error exporting to CSV:", error);
    throw error;
  }
};

/**
 * Formats a date as YYYY-MM-DD
 */
const formatDate = (date: Date): string => {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
};

/**
 * Exports all quotes or quotes from a specific category
 */
export const exportAllQuotes = async (options: QuoteExportOptions): Promise<Quote[]> => {
  try {
    const { mode, categoryId } = options;
    
    // Base query to get all quotes with their categories
    let query = supabase
      .from("quotes")
      .select(`
        *,
        quote_categories (
          category_id,
          categories (
            id,
            name
          )
        )
      `);
    
    // If mode is 'category', filter by category ID
    if (mode === 'category' && categoryId) {
      const quoteIdsInCategory = await supabase
        .from("quote_categories")
        .select("quote_id")
        .eq("category_id", categoryId);
        
      if (quoteIdsInCategory.error) throw quoteIdsInCategory.error;
      
      const quoteIds = quoteIdsInCategory.data.map(item => item.quote_id);
      
      if (quoteIds.length > 0) {
        query = query.in("id", quoteIds);
      } else {
        return []; // No quotes in this category
      }
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    // Transform the data to include formatted categories
    return data.map(quote => {
      const categories = quote.quote_categories
        ? quote.quote_categories.map(qc => ({
            id: qc.category_id,
            name: qc.categories?.name || "",
          }))
        : [];
      
      return {
        id: quote.id,
        content: quote.content,
        author: quote.author,
        source: quote.source,
        created_at: quote.created_at,
        categories
      };
    });
    
  } catch (error) {
    console.error("Error exporting all quotes:", error);
    throw error;
  }
};
